import React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { FaPowerOff, FaShieldAlt } from "react-icons/fa";
import RefreshActionButton from "components/atoms/refresh-action-button";
import adminApi from "services/admin-api";
import { formatRelativeTime, getApiErrorMessage } from "utils/admin";
import { StatusPill } from "components/admin";
import Modal from "components/global/Modal";
import { AdminSession } from "types/api";
import {
  SessionManagementModalSecondaryButton,
  SessionManagementModalDangerButton,
  SessionManagementModalContentStack,
  SessionManagementModalSessionList,
  SessionManagementModalSessionCard,
  SessionManagementModalSessionRow,
  SessionManagementModalSessionDetails,
  SessionManagementModalDeviceName,
  SessionManagementModalInfoRow,
  SessionManagementModalMetaText,
  SessionManagementModalSessionActions,
  SessionManagementModalFooterActions,
  SessionManagementModalGlobalActions,
  SessionManagementModalEmptyStatePanel,
  SessionManagementModalEmptyStateTitle,
  SessionManagementModalEmptyStateDescription,
  SessionManagementModalSkeletonCard,
  SessionManagementModalSkeletonLine,
  SessionManagementModalSkeletonLineSecondary,
  SessionManagementModalSkeletonLineTertiary,
} from "components/global/styles/SessionManagementModal.inline.styles";

type SessionManagementModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onLogoutCurrentRequest: () => void;
  onLogoutAllRequest: () => void;
};

const isCurrentSession = (
  session: AdminSession,
  currentSessionId?: number | string | null,
) => {
  if (currentSessionId !== undefined && currentSessionId !== null) {
    const comparableIds = [session.id, session.session_id]
      .filter((value) => value !== undefined && value !== null)
      .map((value) => String(value));

    if (comparableIds.includes(String(currentSessionId))) {
      return true;
    }
  }

  return Boolean(session.current);
};

const getCurrentSessionId = (
  currentSession?: AdminSession | null,
  listCurrentSessionId?: number | string | null,
) => currentSession?.id ?? currentSession?.session_id ?? listCurrentSessionId ?? null;

const mergeSessionsWithCurrent = (
  sessions: AdminSession[],
  currentSession?: AdminSession | null,
  currentSessionId?: number | string | null,
) => {
  if (!currentSession) {
    return sessions;
  }

  const currentIsListed = sessions.some((session) =>
    isCurrentSession(session, currentSessionId),
  );

  if (currentIsListed) {
    return sessions;
  }

  return [currentSession, ...sessions];
};

const getSessionLabel = (session: AdminSession, current: boolean) => {
  if (current) {
    return "This device";
  }

  const clients = (session.clients || []).filter(Boolean);

  if (clients.length > 0) {
    return clients.join(", ");
  }

  return "Another device";
};

const sortSessions = (
  sessions: AdminSession[],
  currentSessionId?: number | string | null,
) =>
  [...sessions].sort((left, right) => {
    const leftIsCurrent = isCurrentSession(left, currentSessionId);
    const rightIsCurrent = isCurrentSession(right, currentSessionId);

    if (leftIsCurrent && !rightIsCurrent) {
      return -1;
    }

    if (!leftIsCurrent && rightIsCurrent) {
      return 1;
    }

    const leftTimestamp = new Date(left.last_accessed_at || left.created_at || 0).getTime();
    const rightTimestamp = new Date(right.last_accessed_at || right.created_at || 0).getTime();

    return rightTimestamp - leftTimestamp;
  });

const SessionManagementSkeleton: React.FC = () => (
  <SessionManagementModalSessionList aria-label="Loading signed-in devices" role="status">
    {Array.from({ length: 3 }).map((_, index) => (
      <SessionManagementModalSkeletonCard key={index}>
        <SessionManagementModalSkeletonLine />
        <SessionManagementModalSkeletonLineSecondary />
        <SessionManagementModalSkeletonLineTertiary />
      </SessionManagementModalSkeletonCard>
    ))}
  </SessionManagementModalSessionList>
);

export const SessionManagementModal: React.FC<SessionManagementModalProps> = ({
  isOpen,
  onClose,
  onLogoutCurrentRequest,
  onLogoutAllRequest,
}) => {
  const queryClient = useQueryClient();
  const { data: currentSessionPayload, isLoading: isCurrentLoading } = useQuery({
    queryKey: ["session-current"],
    queryFn: () => adminApi.sessions.current(),
    enabled: isOpen,
  });
  const { data: sessionsPayload, isLoading: isSessionsLoading } = useQuery({
    queryKey: ["sessions"],
    queryFn: () => adminApi.sessions.list(),
    enabled: isOpen,
  });
  const refreshMutation = useMutation({
    mutationFn: () => adminApi.sessions.refreshCurrent(),
    onSuccess: () => {
      toast.success("Session renewed.");
      queryClient.invalidateQueries({
        queryKey: ["session-current"],
      });
      queryClient.invalidateQueries({
        queryKey: ["sessions"],
      });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, "Unable to renew this session."));
    },
  });
  const revokeSessionMutation = useMutation({
    mutationFn: (sessionId: string | number) => adminApi.sessions.remove(sessionId),
    onSuccess: () => {
      toast.success("Device signed out.");
      queryClient.invalidateQueries({
        queryKey: ["session-current"],
      });
      queryClient.invalidateQueries({
        queryKey: ["sessions"],
      });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, "Unable to sign out that device."));
    },
  });

  const currentSession = currentSessionPayload?.session;
  const currentSessionId = getCurrentSessionId(
    currentSession,
    sessionsPayload?.current_session_id,
  );
  const sessions = mergeSessionsWithCurrent(
    sessionsPayload?.sessions || [],
    currentSession,
    currentSessionId,
  );
  const sortedSessions = sortSessions(sessions, currentSessionId);
  const isLoading = isCurrentLoading || isSessionsLoading;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Signed-in devices"
      subtitle="See where your account is signed in. Sign out of anything you don't recognize."
      size="lg"
      actions={
        <SessionManagementModalFooterActions>
          {/* TODO: Session renewal should happen automatically in the auth flow; remove this manual action once handled there. */}
          <RefreshActionButton
            ButtonComponent={SessionManagementModalSecondaryButton}
            isLoading={refreshMutation.isPending}
            label="Renew session"
            loadingLabel="Renewing..."
            onClick={() => refreshMutation.mutateAsync()}
          />

          <SessionManagementModalGlobalActions>
            <SessionManagementModalSecondaryButton onClick={onLogoutCurrentRequest} type="button">
              <FaPowerOff size={12} />
              Sign out on this device
            </SessionManagementModalSecondaryButton>
            <SessionManagementModalDangerButton onClick={onLogoutAllRequest} type="button">
              <FaShieldAlt size={12} />
              Sign out everywhere
            </SessionManagementModalDangerButton>
          </SessionManagementModalGlobalActions>
        </SessionManagementModalFooterActions>
      }
    >
      <SessionManagementModalContentStack>
        {isLoading ? (
          <SessionManagementSkeleton />
        ) : sortedSessions.length > 0 ? (
          <SessionManagementModalSessionList>
            {sortedSessions.map((session: AdminSession) => {
              const current = isCurrentSession(session, currentSessionId);
              const lastActiveAt =
                session.last_accessed_at ||
                (current ? currentSession?.last_accessed_at : null) ||
                session.created_at ||
                (current ? currentSession?.created_at : null);
              const ipAddress =
                session.ip_address || (current ? currentSession?.ip_address : null);

              return (
                <SessionManagementModalSessionCard
                  key={String(session.id ?? session.session_id ?? lastActiveAt)}
                >
                  <SessionManagementModalSessionRow>
                    <SessionManagementModalSessionDetails>
                      <SessionManagementModalInfoRow>
                        <SessionManagementModalDeviceName>
                          {getSessionLabel(session, current)}
                        </SessionManagementModalDeviceName>
                        {current ? <StatusPill value="This device" /> : null}
                      </SessionManagementModalInfoRow>
                      <SessionManagementModalMetaText>
                        Last active {formatRelativeTime(lastActiveAt)}
                      </SessionManagementModalMetaText>
                      <SessionManagementModalMetaText>
                        IP address: {ipAddress || "Unknown"}
                      </SessionManagementModalMetaText>
                    </SessionManagementModalSessionDetails>

                    {!current ? (
                      <SessionManagementModalSessionActions>
                        <SessionManagementModalSecondaryButton
                          disabled={revokeSessionMutation.isPending}
                          onClick={() =>
                            revokeSessionMutation.mutate(
                              (session.id ?? session.session_id) as string | number,
                            )
                          }
                          type="button"
                        >
                          <FaPowerOff size={12} />
                          {revokeSessionMutation.isPending &&
                          revokeSessionMutation.variables ===
                            (session.id ?? session.session_id)
                            ? "Signing out..."
                            : "Sign out"}
                        </SessionManagementModalSecondaryButton>
                      </SessionManagementModalSessionActions>
                    ) : null}
                  </SessionManagementModalSessionRow>
                </SessionManagementModalSessionCard>
              );
            })}
          </SessionManagementModalSessionList>
        ) : (
          <SessionManagementModalEmptyStatePanel>
            <SessionManagementModalEmptyStateTitle>
              No signed-in devices found
            </SessionManagementModalEmptyStateTitle>
            <SessionManagementModalEmptyStateDescription>
              Once you sign in again, your active devices will appear here.
            </SessionManagementModalEmptyStateDescription>
          </SessionManagementModalEmptyStatePanel>
        )}
      </SessionManagementModalContentStack>
    </Modal>
  );
};

export default SessionManagementModal;
